import React from 'react';
import { User } from '../types/auth';
import { ForecastAccount, CashFlowItem, FXRate, ForecastSettings, MonthlyProjection, DEFAULT_FORECAST_SETTINGS } from '../types/personalForecast';

/**
 * Global state object for Larry's Help
 * Similar pattern to ConnactiveClub's TylerState
 * Use for simple global flags and state that can trigger React updates
 */

// Callback registry for reactive updates
type UpdateCallback = () => void;
const updateCallbacks: Set<UpdateCallback> = new Set();

export const TylerState = {
  loginError: '',
  welcomeModal: 0,
  carNameA: '',
  carNameB: '',
  housingName: '',
  careerNameA: '',
  careerNameB: '',
  user: null as User | null,
  car: {
    includeFinanceScenario: null as Boolean | null,
    includeLeaseScenario: null as Boolean | null,
    carA: {
      inputs: {} as Record<string, any>,
      outputs: {
        buy: {} as Record<string, any>,
        finance: {} as Record<string, any>,
        lease: {} as Record<string, any>
      }
    },
    carB: {
      inputs: {} as Record<string, any>,
      outputs: {
        buy: {} as Record<string, any>,
        finance: {} as Record<string, any>,
        lease: {} as Record<string, any>
      }
    },
    comparison: {
      winner: '' as string,
      results: {} as Record<string, any>
    }
  },
  housing: {
    inputs: {} as Record<string, any>,
    outputs: {
      purchase: {} as Record<string, any>,
      rental: {} as Record<string, any>,
      comparison: {} as Record<string, any>
    }
  },
  rrsp: {
    inputs: {} as Record<string, any>,
    outputs: {
      projection: {} as Record<string, any>,
      withdrawals: {} as Record<string, any>
    }
  },
  retirement: {
    inputs: {} as Record<string, any>,
    outputs: {
      scenarios: [] as any[],
      optimized: {} as Record<string, any>
    },
    userProfile: null as any,
    results: null as any,
    inflationView: 'normal' as string,
    benefitAgesLocked: null as { cpp: boolean; oas: boolean } | null // PDF: "as entered" vs "chosen by the optimizer"
  },
  career: {
    includeEducationPath: null as Boolean | null,
    includeCurrentCareer: null as Boolean | null,
    careerA: {
      inputs: {} as Record<string, any>,
      outputs: {
        lifetimeEarnings: 0,
        netValue: 0,
        totalTaxes: 0,
        totalBenefits: 0,
        netEducationCost: 0,
        workingYears: 0,
        averageAnnualBeforeTax: 0,
        averageAnnualAfterTax: 0,
        cashFlows: [] as any[]
      }
    },
    careerB: {
      inputs: {} as Record<string, any>,
      outputs: {
        lifetimeEarnings: 0,
        netValue: 0,
        totalTaxes: 0,
        totalBenefits: 0,
        netEducationCost: 0,
        workingYears: 0,
        averageAnnualBeforeTax: 0,
        averageAnnualAfterTax: 0,
        cashFlows: [] as any[]
      }
    },
    comparison: {
      winner: '' as string,
      difference: 0,
      results: {} as Record<string, any>,
      breakevenYear: 0,
      breakevenAge: 0
    }
  },
  payroll: {
    inputs: {} as Record<string, any>,
    outputs: {} as Record<string, any>,
  },
  forecast: {
    accounts: [] as ForecastAccount[],
    cashFlows: [] as CashFlowItem[],
    fxRates: [] as FXRate[],
    settings: { ...DEFAULT_FORECAST_SETTINGS } as ForecastSettings,
    projections: [] as MonthlyProjection[],
    activeSection: 'accounts' as string,
    scenarioId: null as string | null,
    scenarioName: 'My Forecast',
    isDirty: false,
  }
};

/**
 * Triggers all registered React components to re-render
 * Call this after modifying TylerState properties that need UI updates
 */
export const updateReact = () => {
  updateCallbacks.forEach(callback => callback());
};

/**
 * Hook to subscribe a component to TylerState updates
 * Returns a function to force component re-render
 */
export const useTylerStateUpdate = (): (() => void) => {
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    const forceUpdate = () => setTick(tick => tick + 1);
    updateCallbacks.add(forceUpdate);

    return () => {
      updateCallbacks.delete(forceUpdate);
    };
  }, []);

  return () => setTick(tick => tick + 1);
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).TylerState = TylerState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).updateReact = updateReact;
}
